import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { sanitizeRich } from "@/lib/richText";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { FadeUp } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { FollowUs } from "@/components/FollowUs";
import { absUrl, truncate, SITE_URL, LOGO_URL, ogImage } from "@/lib/seo";

export default function NewsArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api.get(`/news/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="news-not-found">
        <Seo title="Article Not Found" path={`/news/${slug}`} noindex />
        <h1 className="font-serif text-4xl">Article not found</h1>
        <Link to="/news" className="mt-6 inline-block text-brand font-semibold">Back to news</Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="news-loading">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground animate-pulse">Loading article</span>
      </div>
    );
  }

  const html = sanitizeRich(article.body);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: truncate(article.excerpt || article.title),
    image: [absUrl(article.image)],
    datePublished: article.published_at,
    author: { "@type": "Organization", name: article.author || "SBS Properties" },
    publisher: {
      "@type": "Organization",
      name: "SBS Properties",
      logo: { "@type": "ImageObject", url: LOGO_URL },
    },
    mainEntityOfPage: `${SITE_URL}/news/${article.slug}`,
  };

  return (
    <article data-testid="news-article-page" className="pt-28 md:pt-36 pb-24">
      <Seo
        type="article"
        path={`/news/${article.slug}`}
        title={article.title}
        description={truncate(article.excerpt || article.title)}
        image={ogImage(article.image)}
        jsonLd={articleLd}
      />
      <div className="mx-auto max-w-3xl px-4 sm:px-8">
        <Link to="/news" data-testid="news-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-brand transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to news
        </Link>

        <FadeUp className="mt-6">
          <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-brand font-bold">{article.category}</span>
          <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08]">{article.title}</h1>
          <p className="mt-4 text-xs text-muted-foreground">
            {formatDate(article.published_at)} · {article.read_time} · {article.author}
          </p>
        </FadeUp>

        {article.image && (
          <FadeUp delay={0.1}>
            <img src={article.image} alt={article.title} className="mt-8 mx-auto block h-auto w-auto max-h-[78vh] max-w-full object-contain border hairline bg-sand" />
          </FadeUp>
        )}

        <FadeUp delay={0.15}>
          <div
            className="mt-8 article-body text-muted-foreground text-base sm:text-lg leading-relaxed"
            data-testid="news-article-body"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </FadeUp>

        <div className="mt-12 pt-8 border-t hairline">
          <FollowUs />
        </div>
      </div>
    </article>
  );
}
